import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated, setAuthToken, getAuthToken, getAuthConfig, getCurrentService } from '../utils/auth';
import AppNav from '../components/AppNav';

function SuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentIntent = searchParams.get('payment_intent');
  const setupIntent = searchParams.get('setup_intent');
  const [loading, setLoading] = useState(true);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [checkoutSessionData, setCheckoutSessionData] = useState(null);
  const [isTrialUser, setIsTrialUser] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    const initializePage = async () => {
      // Get token from URL parameter (passed by Stripe redirect) or localStorage
      const tokenFromUrl = searchParams.get('token');
      if (tokenFromUrl) {
        const decodedToken = decodeURIComponent(tokenFromUrl);
        setAuthToken(decodedToken);
      }

      if (!isAuthenticated()) {
        navigate('/subscriptions');
        return;
      }

      if (!paymentIntent && !setupIntent) {
        navigate('/subscriptions');
        return;
      }

      // Handle redirect_status=failed (3DS / bank redirect that failed during trial setup)
      const redirectStatus = searchParams.get('redirect_status');
      if (redirectStatus === 'failed' && setupIntent) {
        const planSlug = searchParams.get('plan') || '';
        // Notify backend to send card failure email (non-blocking)
        try {
          const { getAuthToken: _gat } = await import('../utils/auth');
          await fetch(`${API_URL}/api/subscriptions/trial-card-failed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${_gat()}` },
            body: JSON.stringify({ setupIntentId: setupIntent, planSlug, errorMessage: 'Card authentication failed during 3D Secure redirect' }),
          });
        } catch (_e) { /* non-critical */ }
        // Redirect back to checkout with error state
        navigate(`/checkout/${planSlug}?auth_failed=1`, { replace: true });
        return;
      }

      // Determine if this is a trial (setup_intent = trial, payment_intent = paid)
      setIsTrialUser(!!setupIntent && !paymentIntent);

      // IDEMPOTENCY CHECK
      const currentIntent = paymentIntent || setupIntent;
      const processedIntentsKey = 'openhosst_processed_intents';
      const processedIntents = JSON.parse(sessionStorage.getItem(processedIntentsKey) || '{}');

      if (processedIntents[currentIntent]) {
        setLoading(false);

        try {
          const intentParams = new URLSearchParams();
          if (setupIntent) intentParams.append('setupIntentId', setupIntent);
          if (paymentIntent) intentParams.append('paymentIntentId', paymentIntent);

          const authToken = getAuthToken();
          if (authToken && authToken.split('.').length === 3) {
            const sessionResponse = await axios.get(
              `${API_URL}/api/checkout-session-by-intent?${intentParams.toString()}`,
              { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            setCheckoutSessionData(sessionResponse.data);
          }
        } catch (err) {
          // Ignore - session data is optional
        }
        return;
      }

      setLoading(true);
      setSavingMetadata(true);

      try {
        await new Promise(resolve => setTimeout(resolve, 500));

        let checkoutSession = null;
        try {
          const intentParams = new URLSearchParams();
          if (setupIntent) intentParams.append('setupIntentId', setupIntent);
          if (paymentIntent) intentParams.append('paymentIntentId', paymentIntent);

          const authToken = getAuthToken();
          if (!authToken || authToken.split('.').length !== 3) {
            throw new Error('Invalid token format');
          }

          const sessionResponse = await axios.get(
            `${API_URL}/api/checkout-session-by-intent?${intentParams.toString()}`,
            { headers: { 'Authorization': `Bearer ${authToken}` } }
          );
          checkoutSession = sessionResponse.data;
          setCheckoutSessionData(checkoutSession);
        } catch (sessionError) {
          // Continue with null checkoutSession
        }

        const storedAction = checkoutSession?.action || 'new';
        const domainChoice = checkoutSession?.domain_choice || null;
        const domain = checkoutSession?.domain || null;
        const subdomain = checkoutSession?.subdomain || null;
        const currentSubscriptionId = checkoutSession?.current_subscription_id || null;

        // For downgrades, cancel the current subscription at period end
        if (storedAction === 'downgrade' && currentSubscriptionId) {
          try {
            await axios.post(
              `${API_URL}/api/subscriptions/cancel`,
              { subscriptionId: currentSubscriptionId },
              { headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } }
            );
          } catch (err) {
            // Don't fail if this fails
          }
        }

        // CREATE SUBSCRIPTION AFTER payment confirmation (except for renewals)
        if (storedAction !== 'renew' && domainChoice) {
          const subscriptionData = {
            paymentIntentId: paymentIntent,
            setupIntentId: setupIntent,
            domainType: domainChoice,
            actionType: storedAction === 'billing_change' ? 'upgrade' : storedAction
          };

          if (domainChoice === 'subdomain') {
            subscriptionData.subdomain = subdomain;
          } else {
            subscriptionData.domain = domain;
          }

          // Include business details if provided
          const storedBusinessDetails = sessionStorage.getItem('openhosst_business_details');
          if (storedBusinessDetails) {
            try {
              subscriptionData.businessDetails = JSON.parse(storedBusinessDetails);
              sessionStorage.removeItem('openhosst_business_details');
            } catch (e) {
              // Ignore parse errors
            }
          }

          // Include promo code if provided at checkout
          const storedPromoCodeId = sessionStorage.getItem('openhosst_promo_code_id');
          if (storedPromoCodeId) {
            subscriptionData.promoCodeId = storedPromoCodeId;
            sessionStorage.removeItem('openhosst_promo_code_id');
          }

          try {
            const response = await axios.post(`${API_URL}/api/create-subscription-after-payment`, subscriptionData, getAuthConfig());

            const currentIntent = paymentIntent || setupIntent;
            const processedIntentsKey = 'openhosst_processed_intents';
            const processedIntents = JSON.parse(sessionStorage.getItem(processedIntentsKey) || '{}');
            processedIntents[currentIntent] = {
              subscriptionId: response.data.subscriptionId,
              timestamp: new Date().toISOString()
            };
            sessionStorage.setItem(processedIntentsKey, JSON.stringify(processedIntents));
            // Prevent back button re-triggering a duplicate subscription
            sessionStorage.setItem('openhosst_subscription_completed', 'true');

            if (checkoutSession?.session_id) {
              try {
                await axios.post(
                  `${API_URL}/api/checkout-session/${checkoutSession.session_id}/complete`,
                  {},
                  { headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` } }
                );
              } catch (completeError) {
                // Don't fail if this fails
              }
            }
          } catch (subscriptionError) {
            throw subscriptionError;
          }
        }

        setSavingMetadata(false);
        setLoading(false);
      } catch (error) {
        setSavingMetadata(false);
        setLoading(false);
      }
    };

    initializePage();
  }, [paymentIntent, setupIntent, navigate, searchParams]);


  // Derive display values from checkout session data
  const getOrderSummary = () => {
    if (!checkoutSessionData) return null;

    const planName = checkoutSessionData.plan_name || checkoutSessionData.plan_slug || 'Subscription';
    const domainChoice = checkoutSessionData.domain_choice;
    const domain = checkoutSessionData.domain;
    const subdomain = checkoutSessionData.subdomain;
    const userEmail = checkoutSessionData.user_email || '';

    let domainText = '';
    if (domainChoice === 'subdomain' && subdomain) {
      domainText = `${subdomain}.oph.st`;
    } else if (domainChoice === 'custom' && domain) {
      domainText = domain;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const billingParam = urlParams.get('billing') || 'monthly';
    const billingCycle = billingParam === 'yearly' ? 'Yearly' : 'Monthly';

    return { planName, domainText, billingCycle, userEmail, domainChoice };
  };

  const orderSummary = getOrderSummary();

  if (loading) {
    return (
      <div className="checkout-container-new">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          {savingMetadata && <p style={{color: '#cccccc', marginTop: '16px'}}>Saving your configuration...</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
      <div className="email-entry-container">
        <div className="email-entry-card">
          <div className="success-icon-animated" style={{marginBottom: '24px'}}>
            <svg className="success-checkmark" viewBox="0 0 52 52">
              <circle className="success-checkmark-circle" cx="26" cy="26" r="25" fill="none"/>
              <path className="success-checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
            </svg>
          </div>

          <h1 className="email-entry-title">Order Confirmed!</h1>

          <p className="email-entry-subtitle" style={{marginBottom: '24px'}}>
            An order confirmation email has been sent to{' '}
            <strong style={{color: '#40E0D0'}}>{orderSummary?.userEmail || 'your email'}</strong>.
            {' '}If you don't see it in your inbox, please check your spam folder.
          </p>

          {/* Order Summary Card */}
          {orderSummary && (
            <div style={{
              background: 'rgba(64, 224, 208, 0.05)',
              border: '1px solid rgba(64, 224, 208, 0.2)',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#ffffff',
                marginBottom: '16px'
              }}>
                Order Summary
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                color: '#cccccc',
                fontSize: '14px'
              }}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{color: '#999'}}>Plan</span>
                  <span style={{color: '#ffffff', fontWeight: '500'}}>{orderSummary.planName}</span>
                </div>
                {orderSummary.domainText && (
                  <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span style={{color: '#999'}}>Domain</span>
                    <span style={{color: '#40E0D0', fontWeight: '500'}}>{orderSummary.domainText}</span>
                  </div>
                )}
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{color: '#999'}}>Billing</span>
                  <span style={{color: '#ffffff', fontWeight: '500'}}>{orderSummary.billingCycle}</span>
                </div>
              </div>
            </div>
          )}

          {/* Trial User Notice */}
          {isTrialUser && (
            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                <i className="fa-solid fa-clock" style={{color: '#fbbf24', fontSize: '20px', marginTop: '2px'}}></i>
                <div style={{flex: 1}}>
                  <p style={{margin: '0 0 8px 0', color: '#fbbf24', fontWeight: '600', fontSize: '14px'}}>
                    Application Review (24-48 hours)
                  </p>
                  <p style={{margin: '0', color: '#d4d4d4', fontSize: '13px', lineHeight: '1.6'}}>
                    Please note it takes 24-48 hours for new applications to be approved (spam prevention).
                    Your 7-day trial starts after approval. Once approved, you will receive your activation email with full access details — check your inbox and spam folder.
                  </p>
                </div>
              </div>
            </div>
          )}



          {/* Custom Domain DNS Notice */}
          {orderSummary?.domainChoice === 'custom' && (
            <div style={{
              background: 'rgba(64, 224, 208, 0.05)',
              border: '1px solid rgba(64, 224, 208, 0.2)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                <i className="fa-solid fa-globe" style={{color: '#40E0D0', fontSize: '20px', marginTop: '2px'}}></i>
                <div style={{flex: 1}}>
                  <p style={{margin: '0 0 4px 0', color: '#40E0D0', fontWeight: '600', fontSize: '14px'}}>
                    Custom Domain Setup
                  </p>
                  <p style={{margin: '0', color: '#d4d4d4', fontSize: '13px', lineHeight: '1.6'}}>
                    DNS setup instructions are available in your portal once your instance is provisioned.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Paid User Notice */}
          {!isTrialUser && (
            <div style={{
              background: 'rgba(64, 224, 208, 0.08)',
              border: '1px solid rgba(64, 224, 208, 0.25)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '12px'}}>
                <i className="fa-solid fa-circle-info" style={{color: '#40E0D0', fontSize: '20px', marginTop: '2px'}}></i>
                <div style={{flex: 1}}>
                  <p style={{margin: '0', color: '#d4d4d4', fontSize: '13px', lineHeight: '1.6'}}>
                    Your application is being provisioned. You will receive your activation email with full access details shortly — check your inbox and spam folder.
                  </p>
                </div>
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/subscriptions')}
            className="btn-continue"
            style={{marginTop: '8px'}}
          >
            Back to OpenHosst
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{flexShrink: 0}}>
              <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* Spam folder notice */}
          <div
            style={{
              marginTop: '24px',
              padding: '14px 18px',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.12) 0%, rgba(245, 158, 11, 0.08) 100%)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
          >
            <i className="fa-solid fa-folder-open" style={{fontSize: '16px', color: '#fbbf24', flexShrink: 0}}></i>
            <p style={{margin: 0, color: '#d4d4d4', fontSize: '13px', lineHeight: '1.5'}}>
              Can't find the email? Check your <strong style={{color: '#fbbf24'}}>Spam</strong> or <strong style={{color: '#fbbf24'}}>Junk</strong> folder.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SuccessPage;
